import React, { useState, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';

const LessonSelector = ({ onSelect }) => {
  const { lessons, setCurrentLesson } = useGameStore();
  const [filter, setFilter] = useState('all'); // all, newbie, elementary, intermediate, advanced
  const [search, setSearch] = useState('');
  const [progress, setProgress] = useState({});
  
  // Load progress from localStorage (in production, fetch from API)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('sb-progress');
      if (saved) {
        setProgress(JSON.parse(saved));
      }
    } catch {}
  }, []);
  
  // Filter lessons
  const filteredLessons = lessons.filter(lesson => {
    // Level filter
    if (filter !== 'all' && lesson.level?.toLowerCase() !== filter) {
      return false;
    }
    
    // Search filter
    if (search) {
      const term = search.toLowerCase();
      return (
        lesson.title.toLowerCase().includes(term) ||
        lesson.code.toLowerCase().includes(term)
      );
    }
    
    // Show all lessons that pass the level/search filters.
    // Dialogue is lazy-loaded after selection, so absence here is expected.
    return true;
  });
  
  const getLessonStatus = (code) => {
    const lessonProgress = progress[code];
    if (!lessonProgress) return { status: 'new', color: '#64748b', label: 'New' };
    if (lessonProgress.completed) return { status: 'completed', color: '#22c55e', label: 'Completed' };
    return { status: 'inProgress', color: '#f59e0b', label: 'In Progress' };
  };
  
  const handleSelect = (index) => {
    setCurrentLesson(index);
    onSelect?.();
  };
  
  const containerStyle = {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '24px',
    minHeight: '100vh',
    background: '#0f172a',
    color: '#e2e8f0',
  };
  
  const headerStyle = {
    marginBottom: '24px',
  };
  
  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: '16px',
  };
  
  const filtersStyle = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  };
  
  const searchInputStyle = {
    flex: 1,
    minWidth: '200px',
    padding: '10px 14px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#e2e8f0',
    fontSize: '0.95rem',
  };
  
  const selectStyle = {
    padding: '10px 14px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#e2e8f0',
    fontSize: '0.95rem',
    cursor: 'pointer',
  };
  
  const statsStyle = {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    fontSize: '0.85rem',
    color: '#94a3b8',
  };
  
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  };
  
  const cardStyle = {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'transform 0.15s, border-color 0.15s',
  };
  
  const cardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  };
  
  const codeStyle = {
    fontSize: '0.8rem',
    color: '#94a3b8',
    fontWeight: 600,
  };
  
  const statusBadgeStyle = (color) => ({
    fontSize: '0.7rem',
    padding: '4px 8px',
    borderRadius: '12px',
    color: 'white',
    fontWeight: 600,
    background: color,
  });
  
  const lessonTitleStyle = {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '12px',
    lineHeight: 1.4,
  };
  
  const metaStyle = {
    display: 'flex',
    gap: '12px',
    fontSize: '0.8rem',
    color: '#94a3b8',
  };
  
  const noResultsStyle = {
    textAlign: 'center',
    padding: '40px',
    color: '#64748b',
  };
  
  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>📚 Choose a Lesson</h1>
        
        <div style={filtersStyle}>
          <input
            type="text"
            placeholder="Search lessons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={searchInputStyle}
          />
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All Levels</option>
            <option value="newbie">Newbie</option>
            <option value="elementary">Elementary</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        
        <div style={statsStyle}>
          <span>Total: {lessons.length}</span>
          <span>Showing: {filteredLessons.length}</span>
          <span>Completed: {Object.values(progress).filter(p => p.completed).length}</span>
        </div>
      </div>
      
      <div style={gridStyle}>
        {filteredLessons.map((lesson, index) => {
          const status = getLessonStatus(lesson.code);
          const originalIndex = lessons.findIndex(l => l.code === lesson.code);
          
          return (
            <div
              key={lesson.code}
              style={cardStyle}
              onClick={() => handleSelect(originalIndex)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#6366f1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#334155';
              }}
            >
              <div style={cardHeaderStyle}>
                <span style={codeStyle}>{lesson.code}</span>
                <span style={statusBadgeStyle(status.color)}>
                  {status.label}
                </span>
              </div>
              
              <h3 style={lessonTitleStyle}>{lesson.title}</h3>
              
              <div style={metaStyle}>
                <span>{lesson.level}</span>
                <span>{lesson.dialogue?.length || 0} sentences</span>
                <span>{lesson.vocabulary?.length || 0} vocab</span>
              </div>
              
              {lesson.audio && (
                <span style={{ fontSize: '0.75rem', marginTop: '8px', display: 'inline-block' }}>
                  🔊 Audio
                </span>
              )}
            </div>
          );
        })}
      </div>
      
      {filteredLessons.length === 0 && (
        <div style={noResultsStyle}>
          No lessons found. Try adjusting your search.
        </div>
      )}
    </div>
  );
};

export default LessonSelector;
